import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
// npm install lucide-react (if not already a dependency)
import { ChevronDown, X, Check } from "lucide-react";
/**
 * ChoiceDropdown — a real combobox, single- and multi-select.
 *
 * Fixes applied from feedback:
 * 1. The box itself is the input. Click it (or just start typing) and it
 *    filters live — no separate "open, then find the search field" step.
 * 2. Selected chips (multi) sit inline in that same box and stay visible
 *    at all times; the panel opens below the WHOLE box (chips included),
 *    so it can no longer cover them.
 * 3. The options panel caps at ~3.5 rows once there are more than 3
 *    options and scrolls internally from there — it can't balloon and
 *    force the modal/page to scroll to reach "Done" anymore.
 * 4. Checkbox/radio glyphs use explicit pixel sizing (18x18) instead of a
 *    Tailwind class (w-4.5) that doesn't exist in the default scale — that
 *    invalid class was silently collapsing to a 0x0 box, which is why it
 *    rendered as a stray dot instead of a checkbox.
 * 5. No "Other" text field lives in here anymore — see QuestionItem below.
 */
const ChoiceDropdown = ({ options, isMulti, selectedIds = [], singleSelected, onToggle, onSelect }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [coords, setCoords] = useState(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef(null);
  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const optionRefs = useRef([]);

  const selectedOptions = isMulti
    ? options.filter((o) => selectedIds.map(String).includes(String(o.id)))
    : options.filter((o) => String(o.id) === String(singleSelected));

  const normalized = search.trim().toLowerCase();
  const filteredOptions = normalized
    ? options.filter(
      (o) => o.option?.toLowerCase().includes(normalized) || o.hindi_translation?.toLowerCase().includes(normalized)
    )
    : options;

  const updateCoords = () => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const gap = 6;
    const rowHeight = 52;
    const desiredHeight = options.length > 3 ? rowHeight * 3.5 : Math.min(rowHeight * options.length, rowHeight * 6);
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const spaceAbove = rect.top - gap;
    const placeAbove = spaceBelow < 160 && spaceAbove > spaceBelow;
    setCoords({
      left: rect.left,
      width: rect.width,
      top: placeAbove ? undefined : rect.bottom + gap,
      bottom: placeAbove ? window.innerHeight - rect.top + gap : undefined,
      maxHeight: Math.max(120, Math.min(desiredHeight, placeAbove ? spaceAbove : spaceBelow)),
    });
  };

  useEffect(() => {
    if (!open) return;
    updateCoords();
    const onReposition = () => updateCoords();
    const onClickOutside = (e) => {
      if (
        containerRef.current && !containerRef.current.contains(e.target) &&
        panelRef.current && !panelRef.current.contains(e.target)
      ) {
        setOpen(false);
        setSearch("");
      }
    };
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);
    document.addEventListener("mousedown", onClickOutside);
    return () => {
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
      document.removeEventListener("mousedown", onClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedOptions.length]);

  // Keep the highlighted row in range as the (filtered) list changes, and
  // scroll it into view so keyboard nav keeps working once the panel scrolls.
  useEffect(() => {
    if (!open) return;
    if (filteredOptions.length === 0) {
      setHighlightedIndex(-1);
      return;
    }
    setHighlightedIndex((i) => (i < 0 || i >= filteredOptions.length ? 0 : i));
  }, [open, filteredOptions.length]);

  useEffect(() => {
    if (open && highlightedIndex >= 0) {
      optionRefs.current[highlightedIndex]?.scrollIntoView({ block: "nearest" });
    }
  }, [open, highlightedIndex]);

  const openPanel = () => {
    setOpen(true);
    if (!isMulti) setSearch("");
    const preselected = options.findIndex((o) =>
      isMulti ? selectedIds.map(String).includes(String(o.id)) : String(o.id) === String(singleSelected)
    );
    setHighlightedIndex(preselected >= 0 ? preselected : 0);
  };

  const closePanel = () => {
    setOpen(false);
    setSearch("");
    setHighlightedIndex(-1);
  };

  const handleOptionClick = (opt) => {
    if (isMulti) {
      onToggle(opt);
      inputRef.current?.focus();
    } else {
      onSelect(opt);
      closePanel();
    }
  };

  const removeChip = (opt, e) => {
    e.stopPropagation();
    onToggle(opt);
  };

  // Full keyboard support: arrows move the highlight, Enter picks it,
  // Escape/Tab close the panel, Backspace on an empty search removes the
  // last chip — the same shortcuts people expect from any tag input.
  const handleKeyDown = (e) => {
    if (!open && ["ArrowDown", "ArrowUp", "Enter"].includes(e.key)) {
      e.preventDefault();
      openPanel();
      return;
    }
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((i) => (filteredOptions.length ? (i + 1) % filteredOptions.length : -1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((i) => (filteredOptions.length ? (i - 1 + filteredOptions.length) % filteredOptions.length : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          handleOptionClick(filteredOptions[highlightedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        closePanel();
        break;
      case "Tab":
        closePanel();
        break;
      case "Backspace":
        if (isMulti && search === "" && selectedOptions.length > 0) {
          onToggle(selectedOptions[selectedOptions.length - 1]);
        }
        break;
      default:
        break;
    }
  };

  const inputValue = isMulti ? search : open ? search : selectedOptions[0]?.option || "";
  const showPlaceholder = isMulti ? selectedOptions.length === 0 && search === "" : !open && selectedOptions.length === 0;

  return (
    <div className="relative">
      <div
        ref={containerRef}
        onClick={() => {
          if (!open) openPanel();
          inputRef.current?.focus();
        }}
        className={`w-full flex flex-wrap items-center gap-1.5 min-h-[42px] px-2.5 py-1.5 rounded-lg border bg-white cursor-text transition-colors ${open ? "border-primary ring-2 ring-primary/15" : "border-gray-300 hover:border-gray-400"
          }`}
      >
        {isMulti &&
          selectedOptions.map((opt) => (
            <span
              key={opt.id}
              className="inline-flex items-center gap-1 bg-primary/10 text-primary text-xs font-medium pl-2.5 pr-1.5 py-1 rounded-md"
            >
              <span className="truncate max-w-[160px]">{opt.option}</span>
              <span
                role="button"
                aria-label={`Remove ${opt.option}`}
                onClick={(e) => removeChip(opt, e)}
                className="hover:bg-primary/20 rounded-full p-0.5 flex-shrink-0"
              >
                <X size={11} strokeWidth={2.5} />
              </span>
            </span>
          ))}
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onFocus={openPanel}
          onKeyDown={handleKeyDown}
          onChange={(e) => {
            setSearch(e.target.value);
            if (!open) setOpen(true);
          }}
          placeholder={showPlaceholder ? (isMulti ? "Select all that apply" : "Select an option") : ""}
          className="flex-1 min-w-[100px] outline-none text-sm bg-transparent py-0.5 text-gray-900"
        />
        <ChevronDown size={16} className={`flex-shrink-0 text-gray-400 transition-transform duration-150 ${open ? "rotate-180" : ""}`} />
      </div>

      {open &&
        coords &&
        createPortal(
          <div
            ref={panelRef}
            style={{ position: "fixed", top: coords.top, bottom: coords.bottom, left: coords.left, width: coords.width, zIndex: 9999 }}
            className="bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
          >
            <div className="overflow-y-auto" style={{ maxHeight: coords.maxHeight }}>
              {filteredOptions.length === 0 && <p className="text-sm text-gray-400 text-center py-4">No matching options</p>}
              {filteredOptions.map((opt, i) => {
                const selected = isMulti
                  ? selectedIds.map(String).includes(String(opt.id))
                  : String(singleSelected) === String(opt.id);
                const highlighted = i === highlightedIndex;
                return (
                  <button
                    key={opt.id}
                    ref={(el) => (optionRefs.current[i] = el)}
                    type="button"
                    onMouseEnter={() => setHighlightedIndex(i)}
                    onClick={() => handleOptionClick(opt)}
                    className={`w-full flex items-center gap-3 px-3.5 py-2.5 text-left text-sm transition-colors ${highlighted ? "bg-primary/10" : selected ? "bg-primary/5" : "hover:bg-gray-50"
                      }`}
                  >
                    {/* Explicit px sizing via inline style here — do not
                        swap this for a fractional Tailwind class like
                        w-4.5, that's what caused the checkbox-as-dot bug
                        originally (it isn't a real utility, so it silently
                        resolved to 0x0 and only the border painted). Color
                        still comes from your existing primary/gray classes. */}
                    <span
                      style={{ width: 18, height: 18 }}
                      className={`flex-shrink-0 ${isMulti ? "rounded-[4px]" : "rounded-full"} border-2 flex items-center justify-center ${selected ? "border-primary bg-primary" : "border-gray-300 bg-white"
                        }`}
                    >
                      {selected && <Check size={11} strokeWidth={3} className="text-white" />}
                    </span>
                    <span className={selected ? "text-primary font-medium" : "text-gray-700"}>
                      {opt.option}
                      {opt.hindi_translation && (
                        <span className="block text-xs font-normal text-gray-500 mt-0.5">{opt.hindi_translation}</span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
            {isMulti && (
              <div className="border-t border-gray-100 px-3.5 py-2.5 flex justify-between items-center bg-gray-50">
                <span className="text-xs text-gray-500">{selectedIds.length} selected</span>
                <button type="button" onClick={closePanel} className="text-xs font-medium text-primary hover:text-primary/80">
                  Done
                </button>
              </div>
            )}
          </div>,
          document.body
        )}
    </div>
  );
};

export default ChoiceDropdown;